'use client'

// "VRS November deals" popup island: the modal plus the side tab it leaves
// behind when dismissed. Copy, photos and the deal sets come from the
// sale-campaign doc (campaign.popup + dealGroups + limitedDeals); the state
// rules live in lib/dealPopup.js.
// This file is only the UI.
//
// Mounted from SiteChrome, so it runs on every page. The modal waits `delayMs`,
// and in opt-in consent regions it also waits for the cookie-consent choice, so
// it never stacks on top of the CMP modal. The tab needs no wait: it is small
// and only exists because the visitor already saw the modal.
//
// Client-only by design: nothing renders until mounted, so the time- and
// storage-dependent output never has to match the server HTML.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import SmartLink from '../SmartLink'
import DealPrice from '../SaleCampaign/DealPrice'
import { formatMoney, formatSaleDay } from '../sections/Sale/saleFormat'
import { pushDataLayerEvent } from '../../utils/dataLayer'
import { isOptInRequired } from '../../utils/consentMode'
import { getCookie } from 'cookies-next'
import { DEFAULT_REGION, REGION_COOKIE, isRegion, pathWithRegion } from '../../utils/regions'
import {
  STATE_NEW,
  STATE_TAB,
  STATE_OFF,
  getDealPopup,
  getPopupState,
  popupDelayMs,
  saveState,
  clearState,
} from '../../lib/dealPopup'
import { splitRemaining } from '../../lib/saleCampaign'
import styles from './styles.module.css'

const CONSENT_COOKIE_RE = /(?:^|;\s*)vrs_cc_cookie=/
const CONSENT_POLL_MS = 1000

const getStorage = () => {
  try {
    return window.localStorage
  } catch (_e) {
    return null
  }
}

// useLayoutEffect on the client (measure before paint), no-op warning-free on the server.
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

// Slim one-line timer for a deal set ("Ends in 06d 23h 54m 12s"), driven by
// the popup's own 1 s clock. Fixed-width digits, so the line never jitters.
const pad = (n) => String(n).padStart(2, '0')
function InlineCountdown({ label, targetMs, nowMs }) {
  const r = splitRemaining(targetMs, nowMs)
  return (
    <span className={styles.countdown} role="timer" aria-live="off">
      <span className={styles.countdownDot} aria-hidden="true"/>
      {label} <strong>{r.days > 0 && `${pad(r.days)}d `}{pad(r.hours)}h {pad(r.minutes)}m {pad(r.seconds)}s</strong>
    </span>
  )
}

const fill = (text, values) => String(text || '').replace(/\{(\w+)\}/g, (_m, k) => (values[k] != null ? values[k] : ''))

export default function DealPopup({ campaign = null, region = null, clockOffsetMs = 0 }) {
  const [open, setOpen] = useState(false)
  const [showTab, setShowTab] = useState(false)
  const [nowMs, setNowMs] = useState(null)
  const popup = campaign && campaign.popup
  const key = popup && popup.key
  const readNow = () => Date.now() + (Number(clockOffsetMs) || 0)

  // Prices need a store. Region pages pass it; content pages (/, /hardware,
  // /november-sales ...) have none in the URL, so resolve it like the region
  // switcher does: the region cookie, else geo-IP (/api/getRegion), else us.
  const [priceRegion, setPriceRegion] = useState(isRegion(region) ? region : null)
  useEffect(() => {
    if (isRegion(region)) {
      setPriceRegion(region)
      return undefined
    }
    const cookieRegion = getCookie(REGION_COOKIE)
    if (isRegion(cookieRegion)) {
      setPriceRegion(cookieRegion)
      return undefined
    }
    let cancelled = false
    fetch('/api/getRegion')
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!cancelled) setPriceRegion(body && isRegion(body.region) ? body.region : DEFAULT_REGION)
      })
      .catch(() => {
        if (!cancelled) setPriceRegion(DEFAULT_REGION)
      })
    return () => {
      cancelled = true
    }
  }, [region])

  useEffect(() => {
    if (!popup) return undefined
    // `?popup=show` forgets this browser's choice, so the modal can be previewed
    // again (QA, or sharing the campaign internally).
    if (new URLSearchParams(window.location.search).get('popup') === 'show') {
      clearState(getStorage(), popup)
    }
    setNowMs(readNow())
    const state = getPopupState({ campaign, nowMs: readNow(), region, pathname: window.location.pathname, storage: getStorage() })
    if (state === STATE_TAB) {
      setShowTab(true)
      return undefined
    }
    if (state !== STATE_NEW) return undefined

    let timer = null
    const consentPending = () => isOptInRequired() && !CONSENT_COOKIE_RE.test(document.cookie)
    const tryOpen = () => {
      if (consentPending()) {
        timer = setTimeout(tryOpen, CONSENT_POLL_MS)
        return
      }
      setOpen(true)
      pushDataLayerEvent('popup_view', { popup: key, trigger: 'auto' })
    }
    timer = setTimeout(tryOpen, popupDelayMs(popup))
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, region, clockOffsetMs])

  // The countdown ticks only while the modal is open.
  useEffect(() => {
    if (!open) return undefined
    const id = setInterval(() => setNowMs(readNow()), 1000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clockOffsetMs])

  // Any way of closing the modal without clicking through leaves the side tab.
  const dismiss = (reason) => {
    saveState(getStorage(), popup, STATE_TAB)
    setOpen(false)
    setShowTab(true)
    pushDataLayerEvent('popup_dismiss', { popup: key, reason })
  }

  const reopen = () => {
    setShowTab(false)
    setNowMs(readNow())
    setOpen(true)
    pushDataLayerEvent('popup_view', { popup: key, trigger: 'tab' })
  }

  const closeTab = () => {
    saveState(getStorage(), popup, STATE_OFF)
    setShowTab(false)
    pushDataLayerEvent('popup_dismiss', { popup: key, reason: 'tab_close' })
  }

  // Clicked through to the deal: no modal and no tab on this browser again.
  const goToDeal = (from) => {
    saveState(getStorage(), popup, STATE_OFF)
    setOpen(false)
    setShowTab(false)
    pushDataLayerEvent('popup_click', { popup: key, from })
  }

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') dismiss('escape')
    }
    document.addEventListener('keydown', onKey)
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Aligned price columns: give every deal's price, strikeout and % badge the
  // widest width of its kind in the popup (CSS vars on the dialog, read by
  // .dealPriceBlock), measured before paint and again once the web fonts are in.
  const dialogRef = useRef(null)
  useIsoLayoutEffect(() => {
    const el = dialogRef.current
    if (!open || !el) return undefined
    const cols = [
      ['--col-price', '> div > :first-child'],
      ['--col-was', '> div > s'],
      ['--col-pct', '> div > :last-child:not(:first-child):not(s)'],
    ]
    const measure = () => {
      cols.forEach(([v]) => el.style.setProperty(v, '0px'))
      const widths = cols.map(([, sel]) => Math.ceil(Math.max(0, ...[...el.querySelectorAll(`.${styles.dealPriceBlock} ${sel}`)].map((n) => n.getBoundingClientRect().width))))
      cols.forEach(([v], i) => el.style.setProperty(v, `${widths[i]}px`))
    }
    measure()
    let alive = true
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (alive) measure() })
    return () => { alive = false }
  }, [open, nowMs === null])

  if (nowMs === null || (!open && !showTab)) return null
  const view = getDealPopup(campaign, nowMs, priceRegion)
  if (!view) return null

  const { live, groups, fromPrice } = view
  // Product photos, each tied to its deal (`itemType`) or deal set (`group`):
  // one whose deal does not run in this store (the Lite Formula wheel in the
  // US) is left out with it.
  const shownTypes = new Set(groups.flatMap((g) => g.deals.map((d) => d.itemType)))
  const shownGroups = new Set(groups.map((g) => g.key))
  const images = (Array.isArray(popup.images) && popup.images.length ? popup.images : (popup.image ? [{ src: popup.image }] : []))
    .filter((img) => (img.itemType ? shownTypes.has(img.itemType) : img.group ? shownGroups.has(img.group) : true))
  const values = { price: formatMoney(fromPrice) || '' }
  const href = region ? pathWithRegion(popup.productPath, region) : popup.productPath

  if (showTab) {
    return (
      <div className={styles.tab}>
        <button
          type="button"
          className={styles.tabOpen}
          onClick={reopen}
          aria-label={`${popup.tabLabel}: ${fill(fromPrice ? popup.tabCta : popup.tabCtaNoPrice, values)}`}
        >
          {/* Black strip on the left edge, the wordmark read bottom to top. */}
          <span className={styles.tabWord} aria-hidden="true">
            <span className={styles.tabBrand}>{popup.brand}</span>
            <span className={styles.tabMonth}>{popup.month}</span>
            <span className={styles.tabTag}>{popup.tag}</span>
          </span>
        </button>
        <button type="button" className={styles.tabClose} onClick={closeTab} aria-label="Hide">
          ×
        </button>
      </div>
    )
  }

  return (
    <div className={styles.backdrop} onClick={() => dismiss('backdrop')} role="presentation">
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vrs-popup-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className={styles.close} onClick={() => dismiss('close')} aria-label="Close">
          ×
        </button>

        {/* One tile per product, stacked: pedals, DFP15, Lite wheel (phones:
            pedals left, the wheels on the right). Each tile is a CSS stage with
            a red line along its bottom; the transparent product cutout is
            centred in it at `scale` of the tile, so it sits in the exact
            middle between the red lines at any popup size. Absolutely
            positioned inside a fixed modal: no layout shift. */}
        <div className={[styles.media, images.length === 2 ? styles.mediaTwo : null].filter(Boolean).join(' ')} aria-hidden="true">
          {images.map((img, i) => (
            <span key={img.src} className={[i === 0 ? styles.mediaMain : styles.mediaTile, img.fill ? styles.mediaFill : null].filter(Boolean).join(' ')} style={img.scale ? { '--cut-scale': img.scale } : undefined}>
              {/* eslint-disable-next-line @next/next/no-img-element, local-rules/prefer-img-component -- decorative campaign still in a fixed overlay */}
              <img src={img.src} alt="" width={img.width || 640} height={img.height || 480} decoding="async" draggable={false} />
            </span>
          ))}
          <span className={styles.mediaPill}>{live ? popup.eyebrowLive : popup.eyebrowTeaser}</span>
        </div>

        <div className={styles.panel}>
          <div className={styles.wordmark}>
            <span className={styles.brand}>{popup.brand}</span>
            <span className={styles.month}>{popup.month}</span>
            <span className={styles.tagWord}>{popup.tag}</span>
          </div>

          <p className={styles.eyebrow}>{fill(live ? popup.subLive : popup.subTeaser, values)}</p>
          <h2 id="vrs-popup-title" className={styles.title}>{popup.title}</h2>

          {/* One block per deal set (pedals, wheels), each with its own timer:
              name, a large deal price, the regular price struck through and the
              % off (the "You save" amount stays on the deals page). */}
          {groups.map((g) => (
            <section key={g.key} className={styles.group} aria-label={g.title || undefined}>
              <div className={styles.groupHead}>
                {/* The set's short name, so its timer fits beside it like the
                    other set's (e.g. "DFP15 & Lite wheel deals"). */}
                {(g.short || g.title) && (
                  <h3 className={styles.groupTitle}>
                    {g.short || g.title}
                    {!g.live && <span className={styles.groupSoon}>{fill(popup.startsOn || 'From {day}', { day: formatSaleDay(g.startMs) })}</span>}
                  </h3>
                )}
                <InlineCountdown
                  label={g.live ? (popup.groupEndsIn || 'Ends in') : (popup.groupStartsIn || 'Starts in')}
                  targetMs={g.targetMs}
                  nowMs={nowMs}
                />
              </div>
              <ul className={styles.deals}>
                {g.deals.map((d) => (
                  <li key={d.itemType} className={styles.deal}>
                    <span className={styles.dealName}>{d.title}</span>
                    <DealPrice price={d.price} regular={d.regular} size="md" theme="light" className={styles.dealPriceBlock} showSave={false}/>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          {popup.lead && <p className={styles.lead}>{fill(popup.lead, values)}</p>}

          {Array.isArray(popup.bullets) && popup.bullets.length > 0 && (
            <ul className={styles.bullets}>
              {popup.bullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          )}

          <SmartLink href={href} className={styles.submit} onClick={() => goToDeal('modal')}>
            {live ? popup.cta : popup.ctaTeaser}
          </SmartLink>
          <button type="button" className={styles.decline} onClick={() => dismiss('decline')}>
            {popup.decline}
          </button>
        </div>
      </div>
    </div>
  )
}
